import { inject } from '@angular/core';
import {
  HttpResponse,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { throwError } from 'rxjs';
import { finalize, tap, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from './loader.component';
import { LocalStorageItemsService } from '../services/local-storage-items.service';
import { ApiUrls } from './apiurls';

// Functional interceptor for modern Angular
/**
 * The CSRF token, when the host page has been given one.
 *
 * Frappe only validates the header when the session already carries a token,
 * which it gets from a desk boot — so a session created purely through this SPA
 * is never checked. It IS checked once the same browser has visited /app, and
 * from then on every POST from here fails with CSRFTokenError. Sending the token
 * whenever one is reachable costs nothing and closes that case; making it
 * reachable in every case needs a whitelisted endpoint on the server, which is
 * out of scope here.
 */
const csrfToken = (): string =>
  (window as any)?.frappe?.csrf_token ||
  (window as any)?.csrf_token ||
  '';

export const apiCallInterceptor: HttpInterceptorFn = (req, next) => {
  const loader = inject(LoaderService);
  const router = inject(Router);
  const toastr = inject(ToastrService);
  // Injected, because the 401 handler below needs the real instance: it used to
  // call the static stub, which throws.
  const localStorageItemsService = inject(LocalStorageItemsService);

  // console.log('API Request URL:', req.url);

  const skipLoaderApis = [ApiUrls.check_record_exists];
  const skipErrorToastApis = [ApiUrls.getting_auth_url, ApiUrls.pwdEncryption, ApiUrls.check_login_password];

  const shouldSkipLoader = skipLoaderApis.some((api) => req.url.includes(api));
  const shouldSkipErrorToast = skipErrorToastApis.some((api) => req.url.includes(api));

  // Show loader only if NOT skipped
  if (!shouldSkipLoader) loader.showLoader('loader1');

  // Clone request with necessary headers
  const token = csrfToken();
  const modifiedReq = req.clone({
    url: req.url.replace(/%/, '%25').replace('#', '%23'),
    setHeaders: {
      'cache-control': 'no-cache',
      // Only when we actually have one: an empty header would fail the
      // comparison outright on any session that does carry a token.
      ...(token ? { 'X-Frappe-CSRF-Token': token } : {}),
    },
    withCredentials: true,
  });

  return next(modifiedReq).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        const res: any = event.body;
        if (res?.message?.success === false && !shouldSkipErrorToast) {
          const errorMsg =
            (res.message.error ?? res?.message?.message)?.toString()?.trim();
          if (errorMsg) {
            toastr.error(errorMsg);
          }
        }
      }
    }),
    finalize(() => {
      // Hide loader when request completes
      loader.hideLoader();
    }),
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401 || err.error?.exc_type === 'CSRFTokenError') {
        toastr.error(err?.error?.message || 'Your session has ended. Please sign in again.');
        // The instance method. This called the static stub, which is an unimplemented
        // placeholder that throws — so on every 401 or CSRF error this handler blew
        // up before clearing anything or redirecting, and the request chain it was
        // supposed to recover died silently instead.
        localStorageItemsService.clearAll();
        router.navigate(['']);
      } else if (err.status === 500) {
        // Account-lockout errors already show a dedicated countdown UI
        // (see login.component.ts) — skip the generic 500 toast for them.
        if (err.error?.exc_type !== 'SecurityException') {
          toastr.error('Internal Error 500');
        }
      } else if (err.status === 403) {
        toastr.error('Insufficient Permission');
        const exception = err.error?.exception ?? '';
        const shouldClearStorage =
          exception.includes('Login to') ||
          exception.includes('is not whitelisted') ||
          err.error?.exc_type === 'PermissionError';
        if (shouldClearStorage) {
          localStorage.clear();
          window.location.reload();
          // localStorage.setItem('logout-event', Date.now().toString());
        }
      } else if (err.status === 409) {
        toastr.error('Already Exists');
      } else if (err.status === 400) {
        toastr.error('Bad Request');
      } else if ((err.status === 417 || err.status === 404) && !shouldSkipErrorToast) {
        try {
          const msg = JSON.parse(
            JSON.parse(err?.error?._server_messages)
          )?.message;
          toastr.error(msg);
        } catch {
          toastr.error('Error occurred');
        }
      }
      return throwError(() => err);
    })
  );
};
